import { computed } from 'vue'
import equal from 'fast-deep-equal'
import { getI18n, getLocalizedEnumItems } from '@/plugins/i18n'

import { useAccountingStore } from '@/stores/accounting.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useClone } from '@/composables/object'

export const pickReportConfig = (config, base) => Object.fromEntries(Object.keys(base).map(key => [key, config?.[key] ?? base[key]]))
// Stored configs may omit a switched-off section or keep it without columns: those come from the base.
export const mergeReportConfig = (config, base) => ({
  ...pickReportConfig(config, base),
  sections: Object.fromEntries(Object.entries(base.sections).map(([key, baseSection]) => {
    const section = config?.sections?.[key]
    return [key, {
      enabled: section?.enabled ?? baseSection.enabled,
      columns: section?.columns?.length ? section.columns : baseSection.columns,
    }]
  })),
})

export const useReportColumnTemplates = () => {
  const accountingStore = useAccountingStore()
  const webSiteStore = useWebSiteStore()
  const { reportColumnGroupEnum, reportColumnPresetEnum } = accountingStore
  const presetNames = new Map(accountingStore.getLocalizedReportColumnPresets().map(preset => [preset.id, preset.name]))

  const templates = computed(() => accountingStore.reportColumnTemplates.map(template => ({
    ...template,
    label: template.name || presetNames.get(template.preset),
  })))

  const templateById = (id) => templates.value.find(template => template.id === id) ?? null
  const templatesOf = (reportType) => templates.value.filter(template => template.document_type === reportType)
  const selectableTemplates = (reportType, selectedId = null) => templatesOf(reportType).filter(template => template.is_available || template.id === selectedId)
  const defaultIdFor = (reportType) => webSiteStore.getDefaultReportColumnTemplate(reportType)
  const defaultTemplateFor = (reportType) => templateById(defaultIdFor(reportType)) ??
    templatesOf(reportType).find(template => template.preset === reportColumnPresetEnum.CLIENT) ??
    selectableTemplates(reportType)[0] ??
    null
  const isDefault = (template) => template.id === defaultIdFor(template.document_type)

  const sectionsOf = (reportType) => accountingStore.reportColumns.sections?.[reportType] || []
  const isVisibleColumn = (column) => webSiteStore.can(webSiteStore.permissionFlagEnum.REPORTS) || column.group !== reportColumnGroupEnum.INTERNAL
  const selectedColumns = (section, config) => {
    const columnsByKey = new Map(section.columns.map(column => [column.key, column]))
    return (config.sections[section.key]?.columns || []).map(key => columnsByKey.get(key)).filter(column => column && isVisibleColumn(column))
  }
  const enabledSections = (config) => sectionsOf(config.document_type).filter(section => config.sections[section.key]?.enabled)
  const columnCount = (config) => enabledSections(config).reduce((total, section) => total + selectedColumns(section, config).length, 0)
  const isConfidential = (config) => enabledSections(config).some(section => selectedColumns(section, config).some(column => column.group === reportColumnGroupEnum.INTERNAL))
  const columnGroupNames = new Map(getLocalizedEnumItems(reportColumnGroupEnum, 'common.report.columnGroups').map(item => [item.id, item.name]))

  const isSameTemplateOutput = (config, printTemplateId, template) => equal(pickReportConfig(config, template.config), template.config) &&
    (template.print_template === null || template.print_template === printTemplateId)

  const { QUOTE, INVOICE, DELIVERY_NOTE } = accountingStore.printSettingTypeEnum
  const documentOptionLabels = {
    [QUOTE]: { include_vat: 'common.includeVat' },
    [INVOICE]: { separate_sources: 'common.separateQuoteAndCostReportRows' },
    [DELIVERY_NOTE]: { group_by_tag: 'common.groupBy.rateCardTags' },
  }
  const documentOptions = (config, disable = false) => Object.entries(documentOptionLabels[config.document_type] || {})
    .map(([key, label]) => ({ key, label: getI18n().t(label), value: config[key], disable }))

  const partsOf = (reportType) => accountingStore.reportColumns.parts?.[reportType] || []
  const enabledParts = (config) => partsOf(config.document_type).filter(part => part.sections.some(key => config.sections?.[key]?.enabled))
  // A part switched on restores the sections its base config enables, or all of them when the base enables none.
  const withParts = (config, partKeys, base = config) => ({
    ...config,
    sections: {
      ...config.sections,
      ...Object.fromEntries(partsOf(config.document_type).flatMap((part) => {
        const restoresAll = !part.sections.some(key => base.sections?.[key]?.enabled)
        return part.sections.map(key => [key, {
          ...config.sections?.[key],
          enabled: partKeys.includes(part.key) && (restoresAll || !!base.sections[key]?.enabled),
        }])
      })),
    },
  })

  return {
    templates,
    templateById,
    templatesOf,
    selectableTemplates,
    defaultIdFor,
    defaultTemplateFor,
    isDefault,
    sectionsOf,
    isVisibleColumn,
    selectedColumns,
    enabledSections,
    columnCount,
    isConfidential,
    columnGroupNames,
    isSameTemplateOutput,
    documentOptions,
    partsOf,
    enabledParts,
    withParts,
  }
}

// config, templateId and printTemplateId are refs; printTemplateId stays null for prints without a print template.
export const usePrintSettings = ({ config, templateId, printTemplateId, defaultPrintTemplateId = null }) => {
  const i18n = getI18n()
  const accountingStore = useAccountingStore()
  const webSiteStore = useWebSiteStore()
  const { templateById, selectableTemplates, defaultTemplateFor, isSameTemplateOutput, columnCount } = useReportColumnTemplates()

  const template = computed(() => templateById(templateId.value))
  const templateOptions = computed(() => (config.value ? selectableTemplates(config.value.document_type, templateId.value) : []))
  const isModified = computed(() => !!config.value && !!template.value && !isSameTemplateOutput(config.value, printTemplateId.value, template.value))
  const canSave = computed(() => webSiteStore.can(webSiteStore.permissionFlagEnum.COMPANY_SETTINGS))
  const canUpdate = computed(() => canSave.value && !!template.value)
  const caption = computed(() => (config.value
    ? `${isModified.value ? i18n.t('common.report.presets.custom') : template.value?.label || ''} · ${i18n.t('common.report.columnsCount', columnCount(config.value))}`
    : ''))

  const apply = (selected) => {
    templateId.value = selected.id
    config.value = { ...useClone(selected.config), document_type: selected.document_type }
    printTemplateId.value = selected.print_template ?? defaultPrintTemplateId
  }
  const restore = (documentType, stored, storedTemplateId) => {
    const storedTemplate = templateById(storedTemplateId)
    const base = storedTemplate?.document_type === documentType ? storedTemplate : defaultTemplateFor(documentType)
    templateId.value = base.id
    config.value = stored?.sections
      ? mergeReportConfig({ ...useClone(stored), document_type: documentType }, base.config)
      : { ...useClone(base.config), document_type: documentType }
  }
  const templatePrintTemplateId = () => (printTemplateId.value === (template.value?.print_template ?? defaultPrintTemplateId) ? template.value?.print_template ?? null : printTemplateId.value)
  const syncAsync = async(id) => {
    await accountingStore.fetchReportColumnTemplatesAsync()
    templateId.value = id
  }
  const saveAsync = async(name) => {
    const { data } = await accountingStore.upsertReportColumnTemplateAsync({
      name, document_type: config.value.document_type, config: config.value, print_template: templatePrintTemplateId(),
    })
    await syncAsync(data.id)
  }
  const updateAsync = async() => {
    await accountingStore.upsertReportColumnTemplateAsync({ id: templateId.value, config: config.value, print_template: templatePrintTemplateId() })
    await syncAsync(templateId.value)
  }

  return {
    template,
    templateOptions,
    isModified,
    canSave,
    canUpdate,
    caption,
    apply,
    restore,
    saveAsync,
    updateAsync,
  }
}
